import { HostContext, usePureEffect, useStateChangeCallbacks } from "@buzzeasy/shared-frontend-utilities";
import { Alert, Spin, theme } from "antd";
import { ReactElement, useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import Header from "../presenters/Header";
import { TranscriptHubContext } from "../providers/TranscriptHubProvider";
import AlertList from "./AlertList";
import MainContainerLayout, { sidebarMaxWidth } from "./MainContainer.Layout";
import SettingSideBar from "./SettingsSideBar";
import TranscriptContainer from "./TranscriptContainer";

const { useToken } = theme;

const desiredContentMinWidth = 300;
export const playingTranslatedAudioToCustomerMessageKey = "ctui-playingTranslatedAudioToCustomer";

export interface MainContainerProps {
  conversationId: string;
  showHeader: boolean;
  onTranscriptRecommended?: (shouldShow: boolean) => void;
}

export default function MainContainer({ conversationId, showHeader, onTranscriptRecommended }: MainContainerProps): ReactElement {
  const { token } = useToken();
  const { t } = useTranslation();
  const { messageApi } = useContext(HostContext);

  const [sidebarExpanded, setSidebarExpanded] = useState(window.innerWidth - sidebarMaxWidth >= desiredContentMinWidth);
  const [isSideBarTransitioning, setIsSideBarTransitioning] = useState(false);

  const handleSidebarExpandedChange = useCallback(
    (expanded: boolean) => {
      setSidebarExpanded(expanded);
      setIsSideBarTransitioning(true);
    },
    [setSidebarExpanded, setIsSideBarTransitioning],
  );

  const { isLoading, isError, isPlayingAudioToCustomer, isTranslationEnabled, mode, isChatOnlyMode, isCustomerAudioEnabled } = useContext(TranscriptHubContext);

  useEffect(
    () => {
      if (mode !== "translation" || !onTranscriptRecommended)
        return;

      const shouldShow = isChatOnlyMode || !isCustomerAudioEnabled;
      onTranscriptRecommended(shouldShow);
    },
    [mode, onTranscriptRecommended, isChatOnlyMode, isCustomerAudioEnabled],
  );

  useStateChangeCallbacks({ isPlayingAudioToCustomer, isTranslationEnabled }, [
    [
      () => messageApi.loading({ key: playingTranslatedAudioToCustomerMessageKey, content: t("input.playingTranslatedAudioToCustomer"), duration: 0 }),
      { trigger: p => p.isPlayingAudioToCustomer, condition: () => isTranslationEnabled },
    ],
    [
      () => messageApi.destroy(playingTranslatedAudioToCustomerMessageKey),
      { trigger: p => !p.isPlayingAudioToCustomer || !p.isTranslationEnabled },
    ],
  ]);

  // cleanup the message when the component unmounts
  usePureEffect(
    (refs) => {
      return () => refs.messageApi.destroy(playingTranslatedAudioToCustomerMessageKey);
    },
    [],
    { messageApi },
  );

  return (
    <>
      <MainContainerLayout
        sidebarExpanded={sidebarExpanded}
        onSidebarExpandedChange={handleSidebarExpandedChange}
        onSidebarTransitioningEnd={() => setIsSideBarTransitioning(false)}
        header={showHeader && <Header conversationId={conversationId} />}
        subheader={<AlertList />}
        sidebar={mode === "translation" && <SettingSideBar sidebarExpanded={isSideBarTransitioning || sidebarExpanded} />}
        content={
          isLoading
            ? <CenteredSpin size="large" />
            : isError
              ? <Alert showIcon type="error" message={t("transcript.conversationNoLongerExists")} style={{ marginTop: token.paddingXXS }} />
              : <TranscriptContainer />
        }
      />
    </>
  );
}

const CenteredSpin = styled(Spin)`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;
